import { useState, useEffect } from 'react';
import { client } from '@/api/client';
import { AlertTriangle, X, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

const daysUntil = (dateStr) => {
  if (!dateStr) return null;
  const diff = new Date(dateStr) - new Date();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

const urgencyClass = (days) => {
  if (days <= 7) return { badge: 'bg-red-500/20 text-red-400 border-red-500/40', label: 'CRITICAL' };
  if (days <= 14) return { badge: 'bg-orange-500/20 text-orange-400 border-orange-500/40', label: 'URGENT' };
  return { badge: 'bg-amber-500/20 text-amber-400 border-amber-500/40', label: 'WARNING' };
};

export default function ExpiryAlertBanner({ onOpenDriver, onOpenAsset }) {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      const [drivers, assets] = await Promise.all([
        client.entities.DriverProfile.filter({ approval_status: 'Approved' }, '-created_date', 200),
        client.entities.Asset.list('-created_date', 200).catch(() => []),
      ]);
      const found = [];

      drivers.forEach(d => {
        const cdlDays = daysUntil(d.cdl_expiration_date);
        if (cdlDays !== null && cdlDays <= 30) {
          found.push({ kind: 'driver', id: d.id, name: d.full_name, type: 'CDL', days: cdlDays, date: d.cdl_expiration_date });
        }
        const medDays = daysUntil(d.med_card_expiration_date);
        if (medDays !== null && medDays <= 30) {
          found.push({ kind: 'driver', id: d.id, name: d.full_name, type: 'Medical Card', days: medDays, date: d.med_card_expiration_date });
        }
      });

      assets.forEach(a => {
        const regDays = daysUntil(a.registration_expiration);
        if (regDays !== null && regDays <= 30) {
          found.push({ kind: 'asset', id: a.id, name: `Unit ${a.unit_number}`, type: `${a.asset_type} Registration`, days: regDays, date: a.registration_expiration });
        }
      });

      // Sort by most urgent first
      found.sort((a, b) => a.days - b.days);
      setAlerts(found);
    };

    fetchAlerts();
  }, []);

  if (alerts.length === 0 || dismissed) return null;

  const handleClick = (a) => {
    if (a.kind === 'driver' && onOpenDriver) onOpenDriver(a.id);
    if (a.kind === 'asset' && onOpenAsset) onOpenAsset(a.id);
  };

  return (
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 gap-3">
        <div className="flex items-center gap-2.5">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <span className="text-sm font-semibold text-amber-400">
            {alerts.length} Document{alerts.length !== 1 ? 's' : ''} Expiring Within 30 Days
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setExpanded(v => !v)}
            className="p-1.5 rounded-lg text-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setDismissed(true)}
            className="p-1.5 rounded-lg text-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Alert rows */}
      {expanded && (
        <div className="border-t border-amber-500/20 divide-y divide-amber-500/10">
          {alerts.map((a, i) => {
            const { badge, label } = urgencyClass(a.days);
            const clickable = (a.kind === 'driver' && onOpenDriver) || (a.kind === 'asset' && onOpenAsset);
            return (
              <div key={i} className="flex items-center justify-between px-4 py-2.5 gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={cn("text-xs font-bold px-2 py-0.5 rounded-full border flex-shrink-0", badge)}>
                    {label}
                  </span>
                  {clickable ? (
                    <button
                      onClick={() => handleClick(a)}
                      title={a.kind === 'driver' ? 'Open driver DQF to update' : 'Open asset to update'}
                      className="text-sm text-foreground font-medium truncate text-left hover:text-amber-300 hover:underline transition-colors"
                    >
                      {a.name}
                    </button>
                  ) : (
                    <span className="text-sm text-foreground font-medium truncate">{a.name}</span>
                  )}
                  <span className="text-xs text-muted-foreground flex-shrink-0">— {a.type}</span>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className={cn(
                    "text-xs font-semibold",
                    a.days <= 0 ? 'text-red-400' : a.days <= 7 ? 'text-red-400' : a.days <= 14 ? 'text-orange-400' : 'text-amber-400'
                  )}>
                    {a.days <= 0 ? 'EXPIRED' : `${a.days}d left`}
                  </span>
                  <p className="text-xs text-muted-foreground">{a.date}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
